import { useEffect, useState, type CSSProperties } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { formatLapTime } from "@vms/leaderboard-ui";
import type { BrandingTemplate } from "@vms/types";
import { decodeResultsPayload, type ResultsQrPayload } from "@vms/types/results-share";

/**
 * Public, key-gated driver results page — the QR target a driver scans at the
 * end of their session (`/r/:eventKey/:sessionId?d=…`). It's the driver's
 * PERSONAL EVENT REPORT: all of their sessions in the event, latest first, with
 * their best lap, leaderboard position, and a link to the full leaderboard.
 *
 * Two data sources (see the plan's "two-source model"):
 *   1. ONLINE — the cloud returns every session the driver ran in this event
 *      (authoritative, live position).
 *   2. OFFLINE — if the cloud has no record (venue was offline at session end,
 *      404) or is unreachable, decode the self-contained `?d=` payload. That
 *      payload is a point-in-time snapshot of the ONE session whose QR was
 *      scanned, so offline shows a single-session report.
 */

interface ResultLap {
  lapNumber: number;
  lapTimeMs: number;
  sector1Ms?: number | null;
  sector2Ms?: number | null;
  sector3Ms?: number | null;
  isValid: boolean;
}

interface ResultSession {
  id: string;
  startedAt?: string | null;
  carName?: string | null;
  bestLapMs: number | null;
  laps: ResultLap[];
}

interface ResultsView {
  eventName: string;
  mode: "hotlap" | "race";
  trackName?: string | null;
  driverName: string;
  companyName?: string | null;
  /** Overall best lap across all the driver's sessions in the event. */
  bestLapMs: number | null;
  position: number | null;
  totalDrivers: number | null;
  /** Latest session first. */
  sessions: ResultSession[];
  branding: BrandingTemplate | null;
  /** Whether the public leaderboard is on (gates the "View full leaderboard" link). */
  leaderboardEnabled: boolean;
  /** True when rendered from the QR payload rather than the cloud. */
  offline: boolean;
}

function fromCloud(d: Record<string, unknown>): ResultsView {
  const event = (d.event ?? {}) as Record<string, unknown>;
  const driver = (d.driver ?? {}) as Record<string, unknown>;
  const sessions = ((d.sessions as Array<Record<string, unknown>>) ?? []).map((s) => ({
    id: (s.id as string) ?? "",
    startedAt: (s.startedAt as string | null) ?? null,
    carName: (s.carName as string | null) ?? (s.car as string | null) ?? null,
    bestLapMs: (s.bestLapMs as number | null) ?? null,
    laps: ((s.laps as ResultLap[]) ?? []).map((l) => ({ ...l, isValid: !!l.isValid })),
  }));
  return {
    eventName: (event.name as string) ?? "Results",
    mode: event.mode === "race" ? "race" : "hotlap",
    trackName: (event.trackName as string | null) ?? (event.track as string | null) ?? null,
    driverName: (driver.displayName as string) ?? "Driver",
    companyName: (driver.companyName as string | null) ?? null,
    bestLapMs: (d.bestLapMs as number | null) ?? null,
    position: (d.position as number | null) ?? null,
    totalDrivers: (d.totalDrivers as number | null) ?? null,
    sessions,
    branding: (d.branding as BrandingTemplate | null) ?? null,
    leaderboardEnabled: (event.leaderboardEnabled as boolean | undefined) !== false,
    offline: false,
  };
}

function fromPayload(p: ResultsQrPayload): ResultsView {
  // The QR payload is a single-session snapshot — wrap it as a one-session report.
  const laps = p.laps.map((l) => ({
    lapNumber: l.n,
    lapTimeMs: l.ms,
    sector1Ms: l.s1 ?? null,
    sector2Ms: l.s2 ?? null,
    sector3Ms: l.s3 ?? null,
    isValid: l.v,
  }));
  return {
    eventName: p.eventName,
    mode: p.mode,
    trackName: p.trackName ?? null,
    driverName: p.driverName,
    companyName: p.companyName ?? null,
    bestLapMs: p.bestLapMs,
    position: p.position,
    totalDrivers: p.totalDrivers,
    sessions: [
      { id: "", startedAt: null, carName: p.carName ?? null, bestLapMs: p.bestLapMs, laps },
    ],
    // The QR branding subset shares the fields LeaderboardShell reads; cast so
    // the provider accepts it (missing fields like customCss are simply absent).
    branding: (p.branding as unknown as BrandingTemplate) ?? null,
    // Offline can't know the leaderboard toggle; show the link (best-effort).
    leaderboardEnabled: true,
    offline: true,
  };
}

function formatSessionDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PublicDriverResults() {
  const { eventKey, sessionId } = useParams<{ eventKey: string; sessionId: string }>();
  const [searchParams] = useSearchParams();
  const encodedPayload = searchParams.get("d");

  const [view, setView] = useState<ResultsView | null>(null);
  const [loading, setLoading] = useState(true);
  // Shrink the logo header once the content scrolls a little.
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    document.title = view ? `Results: ${view.driverName}` : "Results";
    return () => {
      document.title = "VMS Cloud";
    };
  }, [view]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let v: ResultsView | null = null;
      if (eventKey && sessionId) {
        try {
          const res = await fetch(
            `/api/public/results/${encodeURIComponent(eventKey)}/${encodeURIComponent(sessionId)}`,
          );
          if (res.ok) {
            const json = (await res.json()) as { success?: boolean; data?: Record<string, unknown> };
            if (json?.success && json.data) v = fromCloud(json.data);
          }
        } catch {
          /* cloud unreachable — fall through to the QR payload */
        }
      }
      if (!v) {
        const payload = decodeResultsPayload(encodedPayload);
        if (payload) v = fromPayload(payload);
      }
      if (!cancelled) {
        setView(v);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventKey, sessionId, encodedPayload]);

  if (loading) {
    return <div className="min-h-screen bg-gray-950" />;
  }

  if (!view) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950 p-8 text-center text-gray-300">
        <div>
          <p className="text-xl font-semibold">Results unavailable</p>
          <p className="mt-2 text-sm text-gray-500">
            This results link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  const multiSession = view.sessions.length > 1;
  const branding = view.branding;
  const logoUrl = branding?.logoUrl || "/img/fpzero.svg";
  const pageStyle: CSSProperties = {
    ...(branding?.backgroundColor ? { backgroundColor: branding.backgroundColor } : {}),
    ...(branding?.backgroundImageUrl
      ? {
          backgroundImage: `url(${branding.backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }
      : {}),
  };

  // Podium colouring for the position: use the matching tile's background as the
  // "P{n}" text colour (P1→pos1, etc.), unless it equals the page background —
  // then it'd be invisible, so fall back to the tile's text colour, then white.
  const brandBg = (branding?.backgroundColor ?? "").trim().toLowerCase();
  const positionColor = ((): string => {
    const pos = view.position;
    if (pos !== 1 && pos !== 2 && pos !== 3) return "#ffffff";
    const bg =
      pos === 1 ? branding?.pos1BgColor : pos === 2 ? branding?.pos2BgColor : branding?.pos3BgColor;
    const text =
      pos === 1
        ? branding?.pos1TextColor
        : pos === 2
          ? branding?.pos2TextColor
          : branding?.pos3TextColor;
    if (bg && bg.trim().toLowerCase() !== brandBg) return bg;
    return text || "#ffffff";
  })();

  return (
    <div className="flex h-screen flex-col bg-gray-950 text-white" style={pageStyle}>
      {/* Shrinking sticky logo header */}
      <header
        className={`flex-shrink-0 text-center transition-all duration-200 ${
          compact ? "border-b border-white/10 py-2" : "px-8 pt-8 pb-4"
        }`}
      >
        <img
          src={logoUrl}
          alt="Logo"
          className={`mx-auto w-auto object-contain transition-all duration-200 ${
            compact ? "max-h-10" : "max-h-32"
          }`}
          style={{ height: "auto" }}
        />
        <h1
          className={`font-bold tracking-tight transition-all duration-200 ${
            compact ? "mt-1 text-lg" : "mt-3 text-2xl"
          }`}
        >
          {view.eventName}
        </h1>
        {!compact && view.trackName && (
          <p className="mt-1 text-sm text-white/50">{view.trackName}</p>
        )}
      </header>

      {/* Scrollable content */}
      <main
        className="min-h-0 flex-1 overflow-y-auto px-6"
        onScroll={(e) => setCompact(e.currentTarget.scrollTop > 24)}
      >
        <div className="mx-auto w-full max-w-2xl space-y-6 py-6">
          {view.offline && (
            <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-center text-sm text-amber-100">
              These are the results at the moment when you completed your session. Check back
              after the event for the full results.
            </div>
          )}

          {/* Driver + headline result (overall across all sessions) */}
          <div className="rounded-2xl bg-black/30 p-6 text-center backdrop-blur">
            <p className="text-2xl font-bold">{view.driverName}</p>
            {view.companyName && <p className="mt-1 text-sm text-white/70">{view.companyName}</p>}
            <div className="mt-5 flex items-start justify-center gap-8">
              <div>
                <p className="text-xs uppercase tracking-wide text-white/60">Best lap</p>
                <p className="font-mono text-3xl font-bold">
                  {view.bestLapMs ? formatLapTime(view.bestLapMs) : "—"}
                </p>
              </div>
              {view.position != null && (
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wide text-white/60">Position</p>
                  <p className="text-3xl font-bold leading-tight" style={{ color: positionColor }}>
                    P{view.position}
                  </p>
                  {view.totalDrivers ? (
                    <p className="text-sm font-semibold text-white/50">of {view.totalDrivers}</p>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Per-session breakdown, latest first */}
          {view.sessions.map((session, idx) => (
            <div
              key={session.id || idx}
              className="overflow-hidden rounded-2xl bg-black/30 backdrop-blur"
            >
              <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold">
                    {multiSession ? `Session ${view.sessions.length - idx}` : "Session"}
                    {session.carName ? (
                      <span className="font-normal text-white/60"> · {session.carName}</span>
                    ) : null}
                  </p>
                  {session.startedAt && (
                    <p className="text-xs text-white/50">{formatSessionDate(session.startedAt)}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wide text-white/50">Best</p>
                  <p className="font-mono text-sm font-semibold">
                    {session.bestLapMs ? formatLapTime(session.bestLapMs) : "—"}
                  </p>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-white/60">
                    <th className="px-4 py-2">Lap</th>
                    <th className="px-4 py-2">Time</th>
                    <th className="px-4 py-2 text-right">S1</th>
                    <th className="px-4 py-2 text-right">S2</th>
                    <th className="px-4 py-2 text-right">S3</th>
                  </tr>
                </thead>
                <tbody>
                  {session.laps.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-4 text-center text-white/60">
                        No laps recorded.
                      </td>
                    </tr>
                  ) : (
                    session.laps.map((l) => {
                      const isBest =
                        session.bestLapMs != null && l.lapTimeMs === session.bestLapMs && l.isValid;
                      return (
                        <tr
                          key={l.lapNumber}
                          className={`border-b border-white/5 ${isBest ? "bg-white/10 font-semibold" : ""}`}
                        >
                          <td className="px-4 py-2">{l.lapNumber}</td>
                          <td
                            className={`px-4 py-2 font-mono ${l.isValid ? "" : "text-white/40 line-through"}`}
                          >
                            {formatLapTime(l.lapTimeMs)}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-white/70">
                            {l.sector1Ms ? formatLapTime(l.sector1Ms) : "—"}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-white/70">
                            {l.sector2Ms ? formatLapTime(l.sector2Ms) : "—"}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-white/70">
                            {l.sector3Ms ? formatLapTime(l.sector3Ms) : "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ))}

          {/* Link to the full leaderboard — hidden when the public leaderboard is off. */}
          {eventKey && view.leaderboardEnabled && (
            <div className="text-center">
              <Link
                to={`/l/${eventKey}`}
                className="inline-flex items-center justify-center rounded-lg bg-white/15 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/25"
              >
                View full leaderboard →
              </Link>
            </div>
          )}

        </div>
      </main>

      {/* Pinned footer */}
      <footer className="flex-shrink-0 bg-white/60 px-8 py-3 text-center">
        <div className="flex items-center justify-center gap-2">
          <span className="text-xs tracking-wide text-black">Powered by</span>
          <img src="/img/fpzero.svg" alt="FPZERO Simulators" className="h-4 opacity-70" />
        </div>
      </footer>

      {branding?.customCss && <style dangerouslySetInnerHTML={{ __html: branding.customCss }} />}
    </div>
  );
}
